import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCheckTransaction } from './useCheckTransaction'

type Response = {
    uuid: string
    status: number
}

/**
 * Submits the signed transaction, then polls until the chain confirms it.
 *
 * `onConfirmed` fires once, when the deposit actually lands. The caller cannot derive that moment
 * from the mutation itself -- this resolves as soon as the signature is accepted, which is minutes
 * before the money is real.
 */
export const useConfirmTransaction = ({ onConfirmed }: { onConfirmed?: () => void } = {}) => {
    const queryClient = useQueryClient()
    const { mutate: checkTransaction } = useCheckTransaction()

    return useMutation<Response, unknown, unknown>({
        mutationFn: async ({ uuid, signature }) => {
            const resp = await fetch('/api/bank/deposit/solana_usdc/tx', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    uuid,
                    signature,
                }),
            })
            return await resp.json()
        },
        retry: false,
        onSuccess: (data) => {
            queryClient.invalidateQueries(['deposit.solana_usdc.list'])
            const { uuid } = data
            const checkInterval = setInterval(() => {
                checkTransaction(
                    { uuid },
                    {
                        onSuccess: (data) => {
                            const { status } = data
                            if (status === 2) {
                                clearInterval(checkInterval)
                                onConfirmed?.()
                            }
                        },
                    }
                )
            }, 6000)
        },
    })
}
